import { memo, useContext, useState } from 'react';
import _ from 'lodash';
import { Button, Icon } from '@scale/scaleui';
import { Tooltip } from '@scale/scaleui-radix';
import { PinButton } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/PinButton';
import {
  StepWidthWrapper,
  StepWidth,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepWidthWrapper';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import {
  InteractionStepTaskComponent,
  InteractionStepTaskComponentProps,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/InteractionStep';
import {
  CandidateMessageWithMetadata,
  InteractionStepType,
  ModelResponseEditorStepResponse,
  ModelResponseSelectorStepResponse,
} from '@scale/shared/types/chat';
import ToolCallEditor from './ToolCallEditor';
import { initializeStrategy } from './ToolCallStrategies/StrategyRegistry';
import { useStepSubmitHandler } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useStepSubmitHandler';
import { ToolCall, ToolCallStrategy } from './ToolCallStrategies/BaseToolStrategy';
import {
  useChatStoreExtract,
  useMostRecentResponseFullObject,
  useStepParams,
  useStepResponse,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { useTrans } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useTrans';
import { CardAlert } from 'react-scale/components/scalers/tasks/ThreadedInteraction/TempComponents';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';

const initializeToolCalls = (
  toolCallStrategy: ToolCallStrategy,
  candidate: CandidateMessageWithMetadata | undefined,
  existingResponse: ModelResponseEditorStepResponse | undefined,
) => {
  // TODO: This is a hack to get the tool calls from the existing response
  // We should refactor this to be cleaner
  if (candidate?.message && existingResponse?.context.hasToolCalls) {
    const updatedMessage = {
      ...candidate.message,
      content: existingResponse.output,
      messageMetadata: existingResponse.context.messageMetadata,
    };
    return toolCallStrategy.parseToolCalls(updatedMessage);
  }
  if (candidate?.message) {
    return toolCallStrategy.parseToolCalls(candidate.message);
  }
  return [toolCallStrategy.createBlankToolCall()];
};

const NativeToolCallEditorTaskComponent: InteractionStepTaskComponent = ({
  readOnly,
  complete,
  isPinnedView,
  responseIndex,
}: InteractionStepTaskComponentProps) => {
  const { t } = useTrans();
  const stepParams = useStepParams<InteractionStepType.ModelResponseEditor>(responseIndex);
  const taskContext = useContext(ChatTaskContext);
  const shouldResetHistory = taskContext.forceHistoryResetForResponseChanges;

  // Validate that the tool call strategy is set and exists
  if (!stepParams.params.tool_call_strategy) {
    throw new Error('Tool call strategy is required');
  }
  const toolCallStrategy = initializeStrategy(
    stepParams.params.tool_call_strategy,
    stepParams.params.tool_call_strategy_params || {},
  );

  if (!toolCallStrategy) {
    throw new Error(`Received invalid tool call strategy ${stepParams.params.tool_call_strategy}`);
  }

  const markStepAsIncompletedByIndex = useChatStoreExtract(
    state => state.markStepAsIncompletedByIndex,
  );
  const response = useStepResponse<ModelResponseEditorStepResponse>(responseIndex);
  const { onSubmit } = useStepSubmitHandler({
    responseIndex,
  });
  const editorReadOnly = readOnly || complete || false;

  const mostRecentResponseFullObject = useMostRecentResponseFullObject(
    responseIndex,
  ) as ModelResponseSelectorStepResponse;

  let selectedIndex = 0;
  if (
    mostRecentResponseFullObject.context?.selectedIndex !== undefined &&
    mostRecentResponseFullObject.context?.selectedIndex > 0
  ) {
    selectedIndex = mostRecentResponseFullObject.context?.selectedIndex;
  }
  const candidate = mostRecentResponseFullObject.context?.candidates[selectedIndex];
  const originalToolCalls = initializeToolCalls(toolCallStrategy, candidate, response);

  const [rewriteToolCalls, setRewriteToolCalls] = useState(originalToolCalls);

  const addToolCall = () => {
    setRewriteToolCalls([...rewriteToolCalls, toolCallStrategy.createBlankToolCall()]);
  };

  const resetToolCalls = () => {
    setRewriteToolCalls(originalToolCalls);
  };

  function validateToolCalls(toolCalls: ToolCall[]): {
    valid: boolean;
    error_message: string | undefined;
  } {
    if (toolCalls.length === 0) {
      return {
        valid: false,
        error_message: 'Please add at least one tool call',
      };
    }

    if (toolCalls.some(toolCall => !toolCall.functionName)) {
      return {
        valid: false,
        error_message: 'Please select a valid function',
      };
    }

    const invalidToolCalls = toolCalls.filter(
      toolCall => !toolCallStrategy.validateToolCallParams(toolCall),
    );
    if (invalidToolCalls.length > 0) {
      const message = `Invalid Parameters for Tools: ${invalidToolCalls
        .map(toolCall => toolCall.functionName)
        .join(', ')}`;
      return {
        valid: false,
        error_message: message,
      };
    }
    return { valid: true, error_message: undefined };
  }

  const { valid, error_message } = validateToolCalls(rewriteToolCalls);

  const submit = async () => {
    const { message, output } = toolCallStrategy.writeToolCalls(
      candidate.message,
      rewriteToolCalls,
    );
    const isEdited = JSON.stringify(rewriteToolCalls) !== JSON.stringify(originalToolCalls);
    await onSubmit(
      {
        type: InteractionStepType.ModelResponseEditor,
        context: {
          baseResponse: '',
          originalText: '',
          autoCritiqueResponse: '',
          attachments: [],
          reactUIGeneratedUrl: '',
          candidate,
          originalMetadata: candidate.message.messageMetadata,
          messageMetadata: message.messageMetadata,
          hasToolCalls: true,
          modelResponseWasEdited: isEdited,
        },
        output,
      },
      responseIndex,
      stepParams,
      shouldResetHistory,
    );
  };

  return (
    <StepWidthWrapper width={isPinnedView ? StepWidth.Full : StepWidth.Responsive}>
      <CardAlert message={response && !complete ? 'Please save the section below.' : undefined}>
        <StepCard readOnly={readOnly}>
          <StepCard.Header>
            <StepCard.HeaderLeft>
              <StepCard.Icon icon="edit" />
              <StepCard.HeaderColumn>
                <StepCard.Title>{stepParams.title}</StepCard.Title>
                <StepCard.Subtitle>{stepParams.subtitle}</StepCard.Subtitle>
              </StepCard.HeaderColumn>
            </StepCard.HeaderLeft>
            <StepCard.HeaderRight>
              <PinButton responseIndex={responseIndex} />
            </StepCard.HeaderRight>
          </StepCard.Header>
          <StepCard.Body>
            {rewriteToolCalls.map((toolCall, idx) => (
              <div
                key={idx}
                className={`flex flex-row gap-2 items-center border border-neutral-300 rounded-md p-2 my-2`}
              >
                <Button
                  variant="secondary"
                  disabled={rewriteToolCalls.length === 1 || editorReadOnly}
                  onClick={() => setRewriteToolCalls(rewriteToolCalls.filter((_, i) => i !== idx))}
                >
                  <Icon icon="trash" variant="neutral" size="sm" />
                </Button>
                <ToolCallEditor
                  strategy={toolCallStrategy}
                  currentToolCall={toolCall}
                  onChange={newToolCall =>
                    !editorReadOnly &&
                    setRewriteToolCalls(
                      rewriteToolCalls.map((toolCall, i) => (idx === i ? newToolCall : toolCall)),
                    )
                  }
                  readOnly={editorReadOnly}
                />
              </div>
            ))}
          </StepCard.Body>
          <StepCard.Footer className="flex justify-end gap-4 mt-2">
            {!readOnly && complete && (
              <Button
                variant="secondary"
                onClick={() => {
                  const canUpdateCompletedState = response && !_.isNil(responseIndex);
                  if (canUpdateCompletedState) {
                    markStepAsIncompletedByIndex(responseIndex);
                  }
                }}
              >
                {t('edit-step')}
              </Button>
            )}
            {!editorReadOnly && (
              <>
                <Button onClick={resetToolCalls} variant="secondary">
                  Reset Tool Calls
                </Button>
                <Button onClick={addToolCall}>+ Add Tool Call</Button>
                <Tooltip content={error_message}>
                  <Button variant="primary" onClick={submit} disabled={!valid}>
                    <Icon variant="white" icon="check" size="sm" />
                    {t('finish-editing')}
                  </Button>
                </Tooltip>
              </>
            )}
          </StepCard.Footer>
        </StepCard>
      </CardAlert>
    </StepWidthWrapper>
  );
};

export default memo(NativeToolCallEditorTaskComponent);
